// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/ContaContabilSearchCombobox.tsx
//
//  Escolhe uma conta do plano de contas (contábil ou gerencial) SEM pedir ID:
//  busca por código (com ou sem pontos) ou descrição, rótulo "1.1.01.001 — Caixa".
//  O plano inteiro já vem em cache (GET /api/plano-contas[-gerencial], mesma chave
//  de usePlanoContas), então a busca é local — sem ida ao servidor por tecla.
//
//  `apenasAnaliticas`: só contas que recebem lançamento (regra CONT-06).
//  `apenasSinteticas`: só contas-mãe (ex.: "Conta mãe" no cadastro do plano).
// ════════════════════════════════════════════════════════════════════════════

import { useQuery } from "@tanstack/react-query";
import { EntityCombobox } from "@/components/EntityCombobox";
import { apiClient } from "@/lib/apiClient";
import { compararTexto } from "@/lib/consultas";

export interface ContaContabilItem {
  id: number;
  codigoPlanoConta?: string;
  descricaoConta?: string;
  tipoContaId?: number | null;      // 0=Sintética, 1=Analítica
  naturezaContaId?: number | null;
  inativo?: number | null;          // 0=Ativo, 1=Inativo
}

interface ContaContabilSearchComboboxProps {
  value?: number | null;
  onChange?: (id: number | null) => void;
  onSelect?: (conta: ContaContabilItem | null) => void;
  /** Plano contábil (padrão) ou gerencial. */
  tipo?: "contabil" | "gerencial";
  apenasAnaliticas?: boolean;
  apenasSinteticas?: boolean;
  /** Esconde uma conta (ex.: a própria conta em edição, ao escolher a conta mãe). */
  excluirId?: number | null;
  /** Lista também contas inativas (padrão: não). */
  incluirInativas?: boolean;
  limite?: number;
  placeholder?: string;
  permitirLimpar?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

const ENDPOINT = { contabil: "/api/plano-contas", gerencial: "/api/plano-contas-gerencial" } as const;
const CHAVE = { contabil: "plano-contas", gerencial: "plano-contas-gerencial" } as const;

const soDigitos = (s: string) => s.replace(/\D/g, "");
const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Hook do plano em cache (mesma queryKey dos hooks de cadastro — não duplica requisição). */
export function usePlanoContasLookup(tipo: "contabil" | "gerencial" = "contabil") {
  return useQuery({
    queryKey: [CHAVE[tipo]],
    queryFn: async () => {
      const { data } = await apiClient.get<ContaContabilItem[]>(ENDPOINT[tipo]);
      return data;
    },
    staleTime: 60_000,
  });
}

/** "1.1.01.001 — Caixa" (ou só o código/descrição que existir). */
export function rotuloConta(c?: { codigoPlanoConta?: string | null; descricaoConta?: string | null } | null) {
  if (!c) return "";
  const cod = c.codigoPlanoConta ?? "";
  const desc = c.descricaoConta ?? "";
  return cod && desc ? `${cod} — ${desc}` : cod || desc;
}

export function ContaContabilSearchCombobox({
  tipo = "contabil", apenasAnaliticas, apenasSinteticas, excluirId, incluirInativas,
  limite = 80, placeholder = "Selecione a conta...", ...props
}: ContaContabilSearchComboboxProps) {
  const { data: plano = [], dataUpdatedAt } = usePlanoContasLookup(tipo);

  const buscar = async (termo: string) => {
    const t = normalizar(termo);
    // Termo só de dígitos/pontos = código: "11010" acha "1.1.01.001".
    const dig = /^[\d.\s-]+$/.test(termo) ? soDigitos(termo) : "";
    return plano
      .filter((c) =>
        (incluirInativas || c.inativo !== 1)
        && (!apenasAnaliticas || c.tipoContaId === 1)
        && (!apenasSinteticas || c.tipoContaId !== 1)
        && c.id !== excluirId
        && (!t
          || normalizar(c.descricaoConta ?? "").includes(t)
          || (c.codigoPlanoConta ?? "").toLowerCase().startsWith(t)
          || (!!dig && soDigitos(c.codigoPlanoConta ?? "").startsWith(dig))))
      .sort((a, b) => compararTexto(a.codigoPlanoConta ?? "", b.codigoPlanoConta ?? ""))
      .slice(0, limite);
  };

  const obterPorId = async (id: number) => {
    const conta = plano.find((c) => c.id === id);
    if (!conta) throw new Error("Conta não encontrada");
    return conta;
  };

  return (
    <EntityCombobox<ContaContabilItem>
      {...props}
      // A chave muda quando o plano carrega/atualiza: a busca local roda de novo.
      queryKey={`conta-contabil-${tipo}-${apenasAnaliticas ? "a" : ""}${apenasSinteticas ? "s" : ""}-${excluirId ?? 0}-${dataUpdatedAt}`}
      buscar={buscar}
      obterPorId={plano.length ? obterPorId : undefined}
      getId={(c) => c.id}
      getCodigo={(c) => c.codigoPlanoConta}
      getDescricao={(c) => c.descricaoConta ?? ""}
      getExtra={(c) => (c.tipoContaId === 1 ? null : "Sintética")}
      placeholder={placeholder}
      placeholderBusca="Buscar por código ou descrição..."
    />
  );
}
